// ---------------------------------------------------------------------------
// useGridEnterNavigation
//
// GH #532 (follow-up): the client also wants Enter / Shift+Enter to act as a
// *fast entry point and field-to-field navigator* across the planogram
// editor, separate from Tab — and wants this in BOTH views (the Visual
// grid of `.grid-cell` divs, and the Tabular `<DataTable>` of `<tr>` rows),
// behaving identically in each.
//
// Why this can't just be "more tabIndex":
//   - Tab's job is unchanged on purpose — it still walks the page's normal
//     tab order (header buttons, etc.) exactly as before. The earlier fix
//     in this PR was only ever about Tab/Shift+Tab moving correctly *once
//     you're already inside the grid/table*, and that's staying as-is in
//     both views.
//   - Enter has no native "move focus" behavior on a plain <input> (it does
//     nothing, or submits an ancestor <form> if there is one) — there's
//     nothing to "unhide" the way the inactive-cell CSS fix did for Tab.
//     This is a new behavior implemented with a real keydown listener that
//     calls .focus() on the right element.
//   - The two keys are deliberately asymmetric in how they ENTER the
//     grid/table:
//       Tab   -> normal page order; eventually reaches the grid/table the
//                same way it reaches any other focusable element on the
//                page.
//       Enter -> the moment it's pressed anywhere on the page while editing
//                this planogram, jump straight to the first row's first
//                field, regardless of what currently has focus. That's the
//                "let the user start working immediately" requirement.
//     Once focus is already inside the grid/table, Enter and Shift+Enter
//     then behave exactly like Tab/Shift+Tab for stepping between
//     fields/cells.
//
// Why one hook covers both views: each "row unit" (a `.grid-cell` div in
// Visual, a `<tr>` in Tabular) already lays out its fields — Product,
// Price, Min, Max in Visual; Product, EAN, Weight/Volume, Price, Min, Max
// in Tabular — in the exact left-to-right document order they should be
// visited in. So this hook never needs to know about "fields" as a concept
// at all: it just orders the row units (via `getRowOrder`, since Visual
// uses [data-row]/[data-col] attributes and Tabular uses literal DOM order
// of rendered `<tr>`s) and then, for each row unit in order, collects
// whatever's actually focusable inside it in document order. Swap the row
// unit selector and ordering strategy per view; the keydown/focus-stepping
// logic itself is identical and unduplicated.
//
// *** FIX (GH #577): the cold-start "jump to first field" Enter behavior
// fired even while a modal dialog was open. ***
//
// The isInsideForeignOverlay check below already protects Enter once
// focus is PHYSICALLY inside a dialog/sidebar/dropdown/etc — but the
// cold-start branch (no current focus inside the grid/table container at
// all) intentionally jumps to the grid's first field "regardless of what
// currently has focus," by design, for the ordinary case of pressing
// Enter from a header button or empty page area. That same "regardless of
// what currently has focus" reasoning is exactly wrong the moment a modal
// dialog is open: pressing Enter to confirm a selection inside that
// dialog (e.g. confirming a highlighted product suggestion) must never
// instead be hijacked into focusing the BACKGROUND grid's first field.
// hasOpenModalDialog below — the same check useCellArrowNavigation and
// useCellClipboard now also use for their own GH #577 fixes — closes that
// gap: while any modal is open, this hook's listener takes no action at
// all, cold-start jump included.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';

export interface UseGridEnterNavigationOptions {
    /** Ref to the container that wraps every row unit (the grid's scroll
     * container in Visual, or specifically the `<tbody>`-level element in
     * Tabular — NOT the whole `<DataTable>`, which would also pull in
     * header controls). */
    containerRef: React.RefObject<HTMLElement | null>;
    /**
     * CSS selector identifying one "row unit" — `.grid-cell` in Visual
     * (one unit = one planogram cell, laid out with its own Product/Price/
     * Min/Max fields), `tr` in Tabular (one unit = one table row, which —
     * same as Visual — also represents exactly one planogram cell, just
     * with its fields arranged as `<td>` columns instead of stacked divs).
     */
    rowItemSelector: string;
    /**
     * Orders the matched row units. Visual cells carry [data-row]/[data-col]
     * attributes that may not match DOM order once rows are filtered to a
     * single selected row, so they're sorted explicitly; Tabular's `<tr>`s
     * are already rendered in the correct order by the DataTable itself, so
     * its caller can just pass through the querySelectorAll result as-is.
     */
    getRowOrder: (rowUnits: HTMLElement[]) => HTMLElement[];
    /** Enter/Shift+Enter are no-ops while this is true. */
    disabled?: boolean;
}

const FOCUSABLE_FIELD_SELECTOR = 'input, [contenteditable="true"]';

/**
 * *** NEW (GH #577): true while ANY modal dialog is open anywhere on the
 * page, regardless of what's inside it. ***
 *
 * Same selector and rationale as the identical helper in
 * useCellArrowNavigation/useCellClipboard — kept as its own copy here for
 * the same reason those two don't share one either (no existing
 * dependency between these hooks to hang a shared import off of). Keep
 * all three copies in sync if this selector ever needs to change.
 */
const hasOpenModalDialog = (): boolean =>
    !!document.querySelector('.p-dialog-mask, [role="dialog"]');

/**
 * Every focusable field inside the container, in row-unit-then-field
 * document order. Built fresh on every Enter/Shift+Enter press (not
 * cached) since rows/cells can be added, removed, or change mode (e.g.
 * read-only) between key presses.
 */
function collectFocusableFields(
    container: HTMLElement,
    rowItemSelector: string,
    getRowOrder: (rowUnits: HTMLElement[]) => HTMLElement[],
): HTMLElement[] {
    const rowUnits = getRowOrder(
        Array.from(container.querySelectorAll<HTMLElement>(rowItemSelector)),
    );

    const fields: HTMLElement[] = [];
    for (const rowUnit of rowUnits) {
        rowUnit
            .querySelectorAll<HTMLElement>(FOCUSABLE_FIELD_SELECTOR)
            .forEach(field => {
                // Skip anything that isn't really reachable right now —
                // disabled (e.g. read-only mode, or the Product field while
                // not in ADD_PRODUCT mode) or hidden (e.g. a collapsed
                // initialization-mode cell, or a column not currently
                // rendered). A field that's there in the DOM but can't
                // actually take focus must not create a "dead stop" in the
                // sequence.
                const isDisabled = (field as HTMLInputElement).disabled;
                const isHidden = field.offsetParent === null;
                if (!isDisabled && !isHidden) {
                    fields.push(field);
                }
            });
    }
    return fields;
}

export function useGridEnterNavigation({
    containerRef,
    rowItemSelector,
    getRowOrder,
    disabled = false,
}: UseGridEnterNavigationOptions) {
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;

    const rowItemSelectorRef = useRef(rowItemSelector);
    rowItemSelectorRef.current = rowItemSelector;

    const getRowOrderRef = useRef(getRowOrder);
    getRowOrderRef.current = getRowOrder;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;
            if (disabledRef.current) return;

            // *** GH #577: checked before anything else — once a modal
            // dialog is open anywhere on the page, this hook must not act
            // at all, including (especially) the cold-start "jump to the
            // grid's first field" branch below, which by design ignores
            // where focus currently is. See this file's GH #577 header
            // comment for the full rationale.
            if (hasOpenModalDialog()) return;

            const container = containerRef.current;
            if (!container) return;

            const target = event.target as HTMLElement | null;

            // Never hijack Enter inside a <textarea> (multi-line input —
            // Enter is meant to insert a newline there, not move focus) or
            // while a PrimeReact AutoComplete suggestion panel is open
            // (Enter there should confirm the highlighted suggestion, its
            // own normal behavior — stepping focus away mid-selection
            // would cancel that). The product field's panel is rendered by
            // PrimeReact at the document body level, so check broadly.
            if (target?.tagName === 'TEXTAREA') return;
            const hasOpenAutocompletePanel = document.querySelector('.p-autocomplete-panel:not([style*="display: none"])');
            if (hasOpenAutocompletePanel && container.contains(target)) return;

            // *** Don't hijack Enter anywhere outside the grid/table that
            // isn't "just the rest of the page" ***
            //
            // PrimeReact's Dialog/Sidebar/Dropdown/AutoComplete overlays
            // (and this file's own plain custom `.context-menu`) render via
            // a portal to document.body — they are NOT inside containerRef's
            // subtree even while they're open and the user is actively
            // typing in one (e.g. ChangePriceDialog, SchemeSettingsDialog,
            // the rename dialog, AddEditCellSettingsDialog, ProductSidebar).
            // Without this check, `!container.contains(target)` would be
            // true for every Enter press inside any of those, and the
            // "Enter from outside jumps to the first row" behavior below
            // would wrongly steal focus away from a dialog the user is
            // mid-edit in. Anything matching one of these selectors is
            // explicitly left alone — its own Enter handling (form submit,
            // confirm selection, etc.) takes over as normal.
            //
            // *** GH #577 note: the hasOpenModalDialog() check above this
            // block already makes this isInsideForeignOverlay check
            // redundant for PrimeReact Dialogs specifically (any open
            // .p-dialog-mask now short-circuits the whole handler before
            // reaching here) — it's kept as-is for the other overlay types
            // listed (.p-sidebar, .p-overlaypanel, .p-dropdown-panel,
            // .context-menu) that aren't modal dialogs and so aren't
            // covered by hasOpenModalDialog. ***
            const isInsideForeignOverlay = !!target?.closest(
                '.p-dialog, .p-sidebar, .p-overlaypanel, .p-dropdown-panel, .p-autocomplete-panel, .context-menu',
            );
            if (isInsideForeignOverlay) return;

            const fields = collectFocusableFields(
                container,
                rowItemSelectorRef.current,
                getRowOrderRef.current,
            );
            if (fields.length === 0) return;

            const isInsideContainer = !!target && container.contains(target);
            const currentIndex = isInsideContainer ? fields.indexOf(target as HTMLElement) : -1;

            // *** The "fast entry point" behavior ***
            // Pressing Enter from anywhere outside the grid/table (page
            // header buttons, nothing focused at all, a different part of
            // the page entirely) jumps straight to the very first field of
            // the very first row unit — skipping the normal page tab order
            // on purpose, so the user can start working immediately.
            // Shift+Enter from outside has no defined "backwards" target,
            // so it's left alone (does nothing) rather than guessing.
            if (!isInsideContainer) {
                if (event.shiftKey) return;
                event.preventDefault();
                fields[0].focus();
                return;
            }

            // Already inside: Enter/Shift+Enter now behave like Tab/
            // Shift+Tab, stepping through the same flat field list — next
            // row unit's first field after the previous one's last field,
            // and the reverse going backwards. There is no wrap-around
            // past the very first/last field; pressing past either end
            // simply does nothing further (matches how Tab naturally stops
            // at the edges of a tab sequence rather than cycling).
            event.preventDefault();

            const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
            if (nextIndex >= 0 && nextIndex < fields.length) {
                fields[nextIndex].focus();
            }
        };

        // Listen at the document level (capture phase) so this fires
        // regardless of which element on the page currently has focus —
        // including elements outside the container entirely, which is
        // required for the "Enter from anywhere jumps in" behavior.
        // Capture phase also means this runs before any per-component
        // onKeyDown the focused element might have, but those are only
        // ever attached to plain <input>s here (no onKeyDown that does
        // anything with Enter inside ADD_PRODUCT mode in either view —
        // the only other Enter handlers in this codebase are the
        // INITIALIZATION-mode MDB/EVA inputs and unrelated dialog forms,
        // none of which are reachable while this listener's `disabled`
        // condition is false), so there's nothing to preempt unexpectedly.
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [containerRef]);
}